/**
 * ⟦C60 · A-177⟧ **Le grand ménage : un interrupteur par système, lu ICI et nulle part ailleurs.**
 *
 * Matt (03/10/2026) : *« Il faut tout simplifier, garder les grands principes. »* Les systèmes que
 * le ménage coupe (le loyer et les charges, la faillite, la boutique, le logement, les jalons, la
 * gestion de la bibliothèque, les strikes, le bad buzz comme sanction, l'alignement) ne sont PAS
 * supprimés : le code reste, les fichiers restent, les assets restent. Ils sont mis au NEUTRE par
 * le contenu (`content/meta.json → systemes`), exactement comme le Fil ⟦A-57⟧, l'axe Produit
 * ⟦A-62⟧ ou l'escalier ⟦A-66⟧ avant lui.
 *
 * ── Le sens des booléens ──────────────────────────────────────────────────────
 *
 * `true` = le système est ACTIF = le jeu d'avant le ménage. C'est le défaut, partout, y compris
 * quand `meta` est absent (les centaines de fixtures de test n'ont pas de `meta`) : un dossier
 * de contenu qui ne mentionne pas `systemes` est antérieur au ménage et rejoue le jeu d'avant au
 * caractère près. C'est `content/meta.json` qui coupe. Revenir en arrière est une ligne par
 * système, et `scripts/simulate.ts --menage tout` le prouve au `md5` (⟦A-10⟧, ⟦A-15⟧).
 *
 * ── Pourquoi un module, et pas un `if` par endroit ────────────────────────────
 *
 * Cinq sites du moteur ne reçoivent pas le contenu (`applyEffectsInPlace`, `appliquerProgression`,
 * `verifierGameOver`, `chargesDuJour`, `recalculerImage`) et le client en appelle trois. La
 * leçon d'⟦A-66⟧ (« le harnais était aveugle », cinq fois) tient ici en une règle : **un seul
 * lecteur de l'interrupteur**, et un `Systemes` qu'on passe en paramètre, jamais un second
 * parsing du `meta` dans chaque fichier.
 *
 * ── ⟦C60 bis⟧ Les arbitrages de Matt après le ménage, par le MÊME module ───────
 *
 * Deux interrupteurs de plus (`reputation` : la jauge sort de l'écran ; `production_value` : le
 * levier n'existe plus) et quatre RÉGLAGES qui valent leur ancienne valeur par défaut : le prix du
 * respec, les Clicks et l'énergie d'une séance de thérapie, la part des Clicks que la chute garde.
 * Même règle, même défaut neutre : absent, c'est le jeu d'avant ; `content/meta.json` livre les
 * nouvelles valeurs ; `avantLeMenage` rallume tout, réglages compris.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge, aucun `Math.random`.
 */
/**
 * Capital versé au palier des 1 000 abonnés quand le contenu ne dit rien : la valeur d'avant le
 * ménage. ⚠️ Redite volontaire de `CAPITAL_DEPART` (`actions.ts`) : ce module ne doit rien
 * importer d'`actions.ts` (`actions.ts` l'importe) ; `menage.test.ts` verrouille l'égalité.
 */
export const CAPITAL_GRAND_SAUT_DEFAUT = 600;
/**
 * ⟦C60 bis · A-177⟧ **Les prix en Clicks que le ménage 2 sort du jeu**, avec leur valeur d'AVANT en
 * défaut (l'état neutre ⟦A-10⟧). Redites volontaires, comme `CAPITAL_GRAND_SAUT_DEFAUT` : ce module
 * n'importe rien d'`actions.ts` ni de `gameover.ts` (qui l'importent) ; `menage.test.ts` verrouille
 * chaque égalité avec la constante d'origine.
 *
 *  - `RESPEC_CLICKS_DEFAUT` = `RESPEC_COUT_BASE` (`actions.ts`) : 250 ₵, doublés à chaque respec ;
 *  - `THERAPIE_CLICKS_DEFAUT` : la séance coûtait 150 ₵ (table §2.2) ;
 *  - `THERAPIE_ENERGIE_DEFAUT` : et 4 d'énergie ;
 *  - `REPRISE_CLICKS_DEFAUT` = `REPRISE_MONEY_RATIO` (`gameover.ts`) : la chute gardait la moitié des
 *    Clicks du palier.
 */
export const RESPEC_CLICKS_DEFAUT = 250;
export const THERAPIE_CLICKS_DEFAUT = 150;
export const THERAPIE_ENERGIE_DEFAUT = 4;
export const REPRISE_CLICKS_DEFAUT = 0.5;
/**
 * ⟦C60 bis · A-177⟧ Le coussin du producteur, en jours de lignes fixes : `SEUIL_TENSION_J` (`manager.ts`, 14 ✎).
 * Redit ici pour la même raison que les autres (ce module n'importe rien de `manager.ts`, qui l'importe) ;
 * `menage-c60bis.test.ts` verrouille l'égalité.
 */
export const COUSSIN_JOURS_DEFAUT = 14;
/**
 * ⟦C62 · lot moteur · A-179⟧ Publications à avoir faites avant le premier événement : `0` = le jeu d'avant (le premier
 * tirage suit la toute première vidéo). C'est `content/meta.json` (`evenements_apres_publications`) qui le monte.
 */
export const EVENEMENTS_APRES_PUBLICATIONS_DEFAUT = 0;
/**
 * ⟦C62 bis · A-179⟧ Le repos de l'Équipe tant que le joueur n'a rien réglé : `"jamais"` = le jeu d'avant (la santé
 * mentale, c'est au joueur, et « Laisser tourner » s'arrête sur « à bout »). C'est `content/meta.json`
 * (`repos_par_defaut`) qui l'automatise.
 */
export const REPOS_PAR_DEFAUT_DEFAUT = "jamais";
/**
 * ⟦C62 bis · A-179⟧ Aucun événement pendant l'action qui grave la victoire : `false` = le jeu d'avant (l'événement de la nuit du
 * million se tire comme un autre, et s'affichait par-dessus « LE MILLION »). C'est `content/meta.json`
 * (`pas_d_evenement_a_la_victoire`) qui l'allume.
 */
export const PAS_D_EVENEMENT_A_LA_VICTOIRE_DEFAUT = false;
/** Le jeu d'avant le ménage : tout actif, capital de 600. L'état neutre. */
export const SYSTEMES_TOUS_ACTIFS = Object.freeze({
    charges: true,
    faillite: true,
    boutique: true,
    logement: true,
    jalons: true,
    bibliotheque: true,
    strikes: true,
    bad_buzz: true,
    alignement: true,
    reputation: true,
    production_value: true,
    capital_grand_saut: CAPITAL_GRAND_SAUT_DEFAUT,
    respec_clicks: RESPEC_CLICKS_DEFAUT,
    therapie_clicks: THERAPIE_CLICKS_DEFAUT,
    therapie_energie: THERAPIE_ENERGIE_DEFAUT,
    reprise_clicks: REPRISE_CLICKS_DEFAUT,
    coussin_jours: COUSSIN_JOURS_DEFAUT,
});
/**
 * Les interrupteurs d'un contenu. **Absent = actif** : seul un `false` explicite coupe, et un
 * capital qui n'est pas un nombre fini ≥ 0 retombe sur le défaut (jamais un `NaN` dans la caisse).
 */
export function systemes(content) {
    const s = content?.meta?.systemes;
    if (!s)
        return SYSTEMES_TOUS_ACTIFS;
    const capital = typeof s.capital_grand_saut === "number" &&
        Number.isFinite(s.capital_grand_saut) &&
        s.capital_grand_saut >= 0
        ? Math.round(s.capital_grand_saut)
        : CAPITAL_GRAND_SAUT_DEFAUT;
    return {
        charges: s.charges !== false,
        faillite: s.faillite !== false,
        boutique: s.boutique !== false,
        logement: s.logement !== false,
        jalons: s.jalons !== false,
        bibliotheque: s.bibliotheque !== false,
        strikes: s.strikes !== false,
        bad_buzz: s.bad_buzz !== false,
        alignement: s.alignement !== false,
        reputation: s.reputation !== false,
        production_value: s.production_value !== false,
        capital_grand_saut: capital,
        respec_clicks: entierOuDefaut(s.respec_clicks, RESPEC_CLICKS_DEFAUT),
        therapie_clicks: entierOuDefaut(s.therapie_clicks, THERAPIE_CLICKS_DEFAUT),
        therapie_energie: entierOuDefaut(s.therapie_energie, THERAPIE_ENERGIE_DEFAUT),
        reprise_clicks: partOuDefaut(s.reprise_clicks, REPRISE_CLICKS_DEFAUT),
        coussin_jours: entierOuDefaut(s.coussin_jours, COUSSIN_JOURS_DEFAUT),
    };
}
/**
 * ⟦C62 · lot moteur · A-179⟧ **Combien de publications le joueur doit avoir faites avant que le moindre événement se
 * tire** (`meta.evenements_apres_publications`). `0` (le défaut, et l'absence du réglage) = le jeu
 * d'avant, où le premier tirage suit la toute première vidéo ; `content/meta.json` le monte à 3.
 *
 * Lu ICI, comme les interrupteurs, mais HORS de `Systemes` et de `meta.systemes` : ce n'est pas un interrupteur
 * de système (champ de premier niveau du `meta`, comme `rythme_croissance`), et `Systemes` est un contrat que
 * le client recopie (écran, télécommande, tests) et que `SystemesFile` doit refléter à l'identique. Un
 * réglage absurde (négatif, NaN, texte) retombe sur le défaut : jamais une porte fantôme.
 */
export function evenementsApresPublications(content) {
    return entierOuDefaut(content?.meta?.evenements_apres_publications, EVENEMENTS_APRES_PUBLICATIONS_DEFAUT);
}
/**
 * ⟦C62 bis · A-179⟧ **Le repos de l'Équipe quand le joueur n'a rien réglé** (`meta.repos_par_defaut`). `"jamais"` (le
 * défaut, et l'absence du réglage) = le jeu d'avant ; `"therapie"` ou `"tout"` = l'équipe fait dormir et souffler la chaîne
 * d'elle-même quand la santé mentale passe sous le plancher de la cadence.
 *
 * Lu ICI, hors de `Systemes` (comme `evenementsApresPublications` : champ de premier niveau du `meta`, ce n'est pas un
 * interrupteur de système). Une valeur inconnue retombe sur le défaut : jamais un repos fantôme.
 */
export function reposParDefaut(content) {
    const v = content?.meta?.repos_par_defaut;
    return v === "therapie" || v === "tout" || v === "jamais" ? v : REPOS_PAR_DEFAUT_DEFAUT;
}
/**
 * ⟦C62 bis · A-179⟧ **Le tirage d'événement s'abstient-il pendant l'action qui grave la victoire ?** (`meta.pas_d_evenement_a_la_victoire`).
 * `false` (le défaut, et l'absence du réglage) = le jeu d'avant. Lu ICI, hors de `Systemes` : comme
 * `evenementsApresPublications`, c'est un champ de premier niveau du `meta`, pas un interrupteur de système. Seul le booléen
 * `true` l'allume : un texte, un nombre ou `null` retombent sur le défaut.
 */
export function pasDEvenementALaVictoire(content) {
    return content?.meta?.pas_d_evenement_a_la_victoire === true;
}
/** Un entier ≥ 0, ou le défaut (jamais un `NaN`, jamais un prix négatif dans la caisse). */
function entierOuDefaut(v, defaut) {
    return typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.round(v) : defaut;
}
/** Une part dans [0 ; 1], ou le défaut. */
function partOuDefaut(v, defaut) {
    return typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 1 ? v : defaut;
}
/** Ce système est-il actif dans ce contenu ? (Absent ⇒ oui.) */
export function systemeActif(content, id) {
    return systemes(content)[id];
}
/** Les systèmes coupés d'un contenu, dans l'ordre du vocabulaire fermé. Vide = le jeu d'avant. */
export function systemesCoupes(content) {
    const s = systemes(content);
    const ids = [
        "charges",
        "faillite",
        "boutique",
        "logement",
        "jalons",
        "bibliotheque",
        "strikes",
        "bad_buzz",
        "alignement",
        "reputation",
        "production_value",
    ];
    return ids.filter((id) => !s[id]);
}
/**
 * Les ids d'événements que le ménage désactive : l'union des listes de
 * `meta.evenements_par_systeme` pour chaque système COUPÉ. Un système actif n'emporte rien.
 */
export function evenementsDesactives(content) {
    const table = content?.meta?.evenements_par_systeme;
    if (!table)
        return AUCUN_EVENEMENT;
    const coupes = systemesCoupes(content);
    if (coupes.length === 0)
        return AUCUN_EVENEMENT;
    const ids = new Set();
    for (const systeme of coupes) {
        for (const id of table[systeme] ?? [])
            ids.add(id);
    }
    return ids.size === 0 ? AUCUN_EVENEMENT : ids;
}
const AUCUN_EVENEMENT = new Set();
/**
 * ⟦C60 · lot b · A-177⟧ **Les ids que le ménage retire d'une liste de contenu** : l'union des listes de
 * `meta.<table>` pour chaque système COUPÉ. Même règle que `evenementsDesactives`, pour les leçons de
 * Matt (`lecons_par_systeme`) et les dossiers du Guide (`dossiers_par_systeme`). Un système actif
 * n'emporte rien ; sans table, ou sans système coupé, l'ensemble est vide : le jeu d'avant, au caractère près.
 */
export function retiresParLeMenage(content, table) {
    const lignes = content?.meta?.[table];
    if (!lignes)
        return AUCUN_EVENEMENT;
    const coupes = systemesCoupes(content);
    if (coupes.length === 0)
        return AUCUN_EVENEMENT;
    const ids = new Set();
    for (const systeme of coupes) {
        for (const id of lignes[systeme] ?? [])
            ids.add(id);
    }
    return ids.size === 0 ? AUCUN_EVENEMENT : ids;
}
/**
 * Les événements qu'on peut TIRER : ceux du contenu, moins ceux que le ménage désactive. Rend la
 * liste d'origine, **sans copie**, quand rien n'est désactivé : l'état neutre, au caractère près.
 *
 * ⚠️ Ne sert qu'au tirage et aux rendez-vous (`event_schedule`). Un `pending_event` déjà posé
 * dans une run en cours se résout toujours (`evenementParId` ne filtre pas) : on désactive ce
 * qui n'est pas encore arrivé, jamais ce que le joueur tient déjà entre les mains.
 */
export function evenementsTirables(events, content) {
    const off = evenementsDesactives(content);
    if (off.size === 0)
        return events;
    return events.filter((e) => !off.has(e.id));
}
/**
 * Le système dont une action dépend, ou `null` si elle n'en dépend d'aucun. C'est la table que
 * `validateAction` lit pour refuser, en `mecanique_verrouillee`, toute action d'un système
 * coupé : un appel direct à l'API n'ouvre pas ce que le contenu a fermé (le moteur est
 * autoritatif, PLAN §6).
 */
export function systemeDeLAction(action) {
    switch (action.type) {
        case "video_jalon":
            return "jalons";
        case "acheter_objet":
            return "boutique";
        case "retour_parents":
        case "reprendre_studio":
            return "logement";
        case "menage":
        case "remaster":
        case "ranger_serie":
            return "bibliotheque";
        default:
            return null;
    }
}
/**
 * ⟦C60 · A-177⟧ L'alignement que le moteur lit d'un sujet. Coupé, tous les sujets valent `0` :
 * la bande est `neutre`, l'historique ne se remplit que de zéros, l'image de chaîne reste à `0`.
 * Actif, c'est la valeur du sujet, au caractère près.
 */
export function alignementEffectif(alignementDuSujet, sys) {
    return sys.alignement ? alignementDuSujet : 0;
}
/**
 * ⟦C60 · A-177⟧ **Le `meta` d'AVANT le ménage** : `meta.systemes` rallumé (tous les systèmes actifs,
 * capital de 600), l'axe Produit rallumé, aucun événement désactivé. Le reste du `meta`
 * (`content_version`, Fil, équipe, rythme…) est conservé tel quel. Ne mute pas son argument.
 */
export function metaAvantLeMenage(meta) {
    return {
        ...(meta ?? {}),
        systemes: {
            charges: true,
            faillite: true,
            boutique: true,
            logement: true,
            jalons: true,
            bibliotheque: true,
            strikes: true,
            bad_buzz: true,
            alignement: true,
            reputation: true,
            production_value: true,
            capital_grand_saut: CAPITAL_GRAND_SAUT_DEFAUT,
            respec_clicks: RESPEC_CLICKS_DEFAUT,
            therapie_clicks: THERAPIE_CLICKS_DEFAUT,
            therapie_energie: THERAPIE_ENERGIE_DEFAUT,
            reprise_clicks: REPRISE_CLICKS_DEFAUT,
            coussin_jours: COUSSIN_JOURS_DEFAUT,
        },
        evenements_par_systeme: {},
        lecons_par_systeme: {},
        dossiers_par_systeme: {},
        axe_produit_actif: true,
    };
}
/**
 * ⟦C60 bis · A-177⟧ **Les prix en Clicks des 12 nœuds de l'Arbre, d'AVANT le ménage 2.** Matt (03/10) :
 * *« Les Clicks ne paient plus que le Marché et l'Équipe »* : `content/skills.json` met ces douze
 * `cout_euros` à `0` (le prix en points de compétence, lui, ne bouge pas : il suit déjà le tier de
 * chaque nœud, comme ses voisins). Cette table est la mémoire de ce qu'ils valaient : `avantLeMenage`
 * la reposte sur les nœuds, ce qui rend la bascule réversible sans retoucher le contenu (rien n'est
 * supprimé), et permet à `simulate.ts --menage=tout` de rejouer le jeu d'avant tout entier.
 */
export const PRIX_EN_CLICKS_AVANT = Object.freeze({
    webcam: 50,
    ring_light: 80,
    le_setup: 300,
    b_roll: 400,
    multicam: 800,
    plateau: 3000,
    monteur_salarie: 1000,
    merch: 200,
    l_agent: 500,
    le_studio: 2000,
    le_producteur: 1500,
    le_manager: 4000,
});
/**
 * ⟦C60 bis⟧ Les mêmes nœuds, avec leur prix en Clicks d'AVANT (`PRIX_EN_CLICKS_AVANT`) reposé là où
 * le contenu l'a mis à zéro. Un nœud qui a déjà un prix, ou qui n'est pas dans la table, est rendu
 * tel quel. Ne mute pas son argument ; rend la même liste (même référence) quand rien ne change.
 */
export function avecPrixEnClicksDAvant(skills) {
    let change = false;
    const rendu = skills.map((s) => {
        const avant = PRIX_EN_CLICKS_AVANT[s.id];
        if (avant === undefined || s.cout_euros === avant)
            return s;
        change = true;
        return { ...s, cout_euros: avant };
    });
    return change ? rendu : skills;
}
/**
 * ⟦C60 · A-177⟧ **Le contenu d'AVANT le ménage** : le même contenu, avec `meta.systemes` rallumé
 * (tous les systèmes actifs, capital de 600), l'axe Produit rallumé et aucun événement désactivé.
 * C'est le bras témoin du ménage, `simulate.ts --menage=tout` le joue, et le moyen, pour les
 * tests des systèmes coupés (loyer, boutique, logement, jalons, strikes…), de continuer à prouver
 * que ces systèmes marchent TOUJOURS quand on les rallume : un système coupé n'est pas un système
 * mort, et c'est ce qui rend le ménage réversible en une ligne.
 *
 * ⟦C60 bis⟧ Il rend aussi leurs prix en Clicks aux 12 nœuds de l'Arbre (`PRIX_EN_CLICKS_AVANT`) et,
 * par `systemes`, le respec à 250 ₵, la thérapie à 150 ₵ et 4 d'énergie, la production value et la
 * chute qui garde la moitié des Clicks. Le rythme de la partie, lui, n'est pas du ménage (`meta` le
 * garde, `simulate.ts --rythme` le rejoue).
 *
 * Ne mute pas son argument.
 */
export function avantLeMenage(content) {
    const rendu = { ...content, meta: metaAvantLeMenage(content.meta) };
    if (content.skills) {
        rendu.skills = avecPrixEnClicksDAvant(content.skills);
    }
    return rendu;
}
//# sourceMappingURL=menage.js.map